import {
  ROTULO_PERCEPCAO,
  ROTULO_STATUS_LEAD,
  ROTULO_TEMPERATURA,
  type OrigemLead,
  type Percepcao,
  type StatusAgendamento,
  type StatusLead,
  type Temperatura,
  type TipoInteracao,
} from '@baiocchi/shared'
import type { TomEtiqueta } from '@baiocchi/ui'

/**
 * Rótulos e tons de domínio.
 *
 * Os rótulos de status, percepção e temperatura vêm de `@baiocchi/shared`, e não
 * são redigitados aqui: o CSV exportado é montado no servidor e precisa do mesmo
 * texto que a tela mostra (RNF-111). O que este arquivo acrescenta é o **tom
 * visual**, que é decisão de interface e não pertence ao contrato.
 */

export { ROTULO_PERCEPCAO, ROTULO_STATUS_LEAD, ROTULO_TEMPERATURA }

export const TOM_STATUS_LEAD: Readonly<Record<StatusLead, TomEtiqueta>> = {
  EM_ABERTO: 'info',
  EM_ATENDIMENTO: 'marca',
  AGENDADO: 'abordagem',
  PROPOSTA: 'neutro',
  VENDA_REALIZADA: 'sucesso',
  VENDA_NAO_REALIZADA: 'perigo',
  FINALIZADO: 'neutro',
}

/**
 * Cor da temperatura.
 *
 * `01-screens.md` (TELA-02) registra o legado como azul / vermelho / verde /
 * verde. Aqui `ABORDAGEM` recebe âmbar em vez do segundo verde: duas colunas de
 * cor idêntica lado a lado eliminam a leitura por cor, que é justamente para que
 * a coluna é colorida. Âmbar também carrega o significado certo — houve tentativa
 * de contato e o cliente não respondeu.
 * ⚠️ Confirmar com a operação; trocar é uma linha aqui e uma no `tokens.css`.
 */
export const TOM_TEMPERATURA: Readonly<Record<Temperatura, TomEtiqueta>> = {
  FRIO: 'frio',
  QUENTE: 'quente',
  ABORDAGEM: 'abordagem',
  EM_CONTATO: 'em-contato',
}

export const COR_TEMPERATURA: Readonly<Record<Temperatura, string>> = {
  FRIO: 'var(--cor-frio)',
  QUENTE: 'var(--cor-quente)',
  ABORDAGEM: 'var(--cor-abordagem)',
  EM_CONTATO: 'var(--cor-em-contato)',
}

export const TOM_PERCEPCAO: Readonly<Record<Percepcao, TomEtiqueta>> = {
  NAO_IDENTIFICADO: 'neutro',
  POTENCIAL_PEQUENO: 'neutro',
  POTENCIAL_MEDIO: 'info',
  POTENCIAL_GRANDE: 'sucesso',
}

export const ROTULO_ORIGEM: Readonly<Record<OrigemLead, string>> = {
  MANUAL: 'Cadastro manual',
  META_LEADS: 'Meta Leads',
  IMPORTACAO: 'Importação',
  PORTAL: 'Portal',
  API: 'API',
  LAIS: 'Lais (qualificado)',
}

export const ROTULO_STATUS_AGENDAMENTO: Readonly<Record<StatusAgendamento, string>> = {
  AGENDADO: 'Agendado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  NAO_COMPARECEU: 'Não compareceu',
  REAGENDADO: 'Reagendado',
}

export const TOM_STATUS_AGENDAMENTO: Readonly<Record<StatusAgendamento, TomEtiqueta>> = {
  AGENDADO: 'info',
  CONCLUIDO: 'sucesso',
  CANCELADO: 'neutro',
  NAO_COMPARECEU: 'perigo',
  REAGENDADO: 'atencao',
}

export const ROTULO_TIPO_INTERACAO: Readonly<Record<TipoInteracao, string>> = {
  CRIACAO: 'Lead cadastrado',
  ATENDIMENTO_MANUAL: 'Atendimento registrado',
  MUDANCA_STATUS: 'Mudança de situação',
  TRANSFERENCIA: 'Transferência',
  AGENDAMENTO_CRIADO: 'Agendamento criado',
  AGENDAMENTO_CONCLUIDO: 'Agendamento concluído',
  AGENDAMENTO_CANCELADO: 'Agendamento cancelado',
  PROPOSTA_CRIADA: 'Proposta criada',
  IMPORTACAO: 'Lead importado',
  INTEGRACAO_RECEBIDA: 'Lead recebido por integração',
  WHATSAPP_ABERTO: 'Conversa de WhatsApp aberta',
  FINALIZACAO_AUTOMATICA: 'Finalizado automaticamente',
  AVALIACAO_RECEBIDA: 'Avaliação recebida',
}

export const TOM_TIPO_INTERACAO: Readonly<
  Record<TipoInteracao, 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo' | 'info'>
> = {
  CRIACAO: 'marca',
  ATENDIMENTO_MANUAL: 'info',
  MUDANCA_STATUS: 'marca',
  TRANSFERENCIA: 'atencao',
  AGENDAMENTO_CRIADO: 'info',
  AGENDAMENTO_CONCLUIDO: 'sucesso',
  AGENDAMENTO_CANCELADO: 'perigo',
  PROPOSTA_CRIADA: 'info',
  IMPORTACAO: 'neutro',
  INTEGRACAO_RECEBIDA: 'neutro',
  WHATSAPP_ABERTO: 'sucesso',
  FINALIZACAO_AUTOMATICA: 'neutro',
  AVALIACAO_RECEBIDA: 'atencao',
}

/**
 * Ajuda do `?` ao lado de "Situação do Lead" (TELA-05).
 *
 * A tela lista só as transições que o servidor devolveu em
 * `transicoesPermitidas` — a máquina de estados tem uma definição só
 * (`transicoes.ts`), e replicá-la no cliente criaria a segunda.
 */
export const AJUDA_SITUACAO =
  'A lista mostra apenas as situações permitidas a partir da situação atual deste lead. ' +
  'Reenviar a situação atual registra apenas uma observação, sem mudar o estado. ' +
  'Algumas situações pedem campos extras: "Agendado" exige data e motivo; ' +
  '"Venda não realizada" exige o motivo; "Venda realizada" exige a data do fechamento.'

/** Faixas do bloco "Aguardando" — escala de urgência, não série categórica. */
export const COR_FAIXA_ESPERA: Readonly<Record<string, string>> = {
  ATE_30_MIN: 'var(--estado-bom)',
  ATE_2_H: 'var(--estado-atencao)',
  ATE_6_H: 'var(--estado-grave)',
  MAIS_DE_1_DIA: 'var(--estado-critico)',
}
